/**
 * Single source of truth for every price on the site (plan/10 §4). The two
 * decided figures live here as constants; the discount percentage shown in copy
 * is DERIVED from them, so the three can never drift.
 * Figures are the decided/placeholder values from plan/00 §6 (D3–D6) — keep in
 * step with the PocketBase `config` collection (founder_cap, offsite_orders,
 * delivery_estimate).
 */
const ANNUAL_USD = 900; // D3 — decided 2026-09-19 (regular annual price at launch)
const PRESALE_USD = 400; // D3 — decided 2026-09-19 (presale year, matches the Creem product)

export const pricing = {
	currency: 'USD',
	/** Presale: one year, paid once today. */
	founderYear: PRESALE_USD, // 400
	/** Annual renewal price locked for the original one-seat founder license. */
	founderRenewal: PRESALE_USD, // 400 — same as the prepaid first year
	/** Regular annual price after launch (the strike-through anchor). */
	annual: ANNUAL_USD, // 900
	/** Launch quarterly term (mentioned, not sold here). D3 recommended. */
	quarterly: 275,
	/** Post-launch extra seat / year (mentioned, not sold here). D3 recommended. */
	extraSeatPerYear: 300,
	/**
	 * Real presale cap (D4 placeholder). /api/seats counts PocketBase orders
	 * plus `config.offsite_orders` (licenses sold under separate agreements,
	 * outside the site) against it; the UI shows whatever the server says and
	 * never adds anything on top.
	 */
	founderCap: 200,
	/** Estimated delivery window shown site-wide (D6, decided 2026-08-28). */
	deliveryWindow: 'December 2026',
	/** Whole percent saved vs. the launch price, rounded DOWN so copy never overstates. */
	discountPct: Math.floor((1 - PRESALE_USD / ANNUAL_USD) * 100) // 55
} as const;

/** `$900`, `$400` — the one place USD amounts get formatted. */
export function usd(amount: number): string {
	return `$${amount.toLocaleString('en-US')}`;
}

/** The presale pricing card (plan/04 §06, plan/05 §2). */
export const pricingCard = {
	eyebrow: 'Presale',
	price: usd(pricing.founderYear), // $400
	priceSuffix: 'for your first year',
	/** The next tier on the scarcity ladder — what buyers are pre-ordering against. */
	nextPrice: usd(pricing.annual), // $900
	nextPriceSuffix: '/yr',
	nextPriceLabel: 'Public launch price',
	saveLine: `${usd(pricing.founderYear)} for your first year and every annual renewal. Public launch price: ${usd(pricing.annual)}/yr.`,
	bullets: [
		'12 months of full access, starting the day we deliver',
		'One seat (more seats available after launch)',
		'Presale number, monthly build notes, roadmap voice',
		`Annual renewal stays ${usd(pricing.founderRenewal)} for this license`,
		'Does not auto-renew. You choose whether to renew.',
		'Fully refundable until your access begins'
	],
	/** Mono footnote under the card. */
	footnote: `Founder renewal: ${usd(pricing.founderRenewal)} / yr, price locked · public launch: ${usd(pricing.annual)} / yr · extra seats sold separately`,
	/** Within 40px of every buy button (plan/05 §2.4). */
	refundLine: 'Refundable in full until your access begins. One email, no questions.',
	deliveryLine: `Access begins at delivery (estimated ${pricing.deliveryWindow}). Your 12 months start then.`,
	taxLine: 'Taxes calculated at checkout.',
	cta: `Pre-order & lock ${usd(pricing.founderRenewal)}/yr`,
	waitlistPrompt:
		'Not ready to pre-order? Get the launch note: one email when it ships, one when the presale licenses run out. Nothing else.'
} as const;
